import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CopyableText from 'components/atoms/CopyableText';
import { JobParamsJsonTree } from 'components/Pages/Jobs/JobParamsJsonTree';
import { Job, JobResponse, JobStatus, JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { castArray, concat, includes, isEmpty, map, some, uniqBy } from 'lodash';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDateTimeForUser } from 'utils/dateTime';
import { humanize } from 'utils/helpers';
import { useJob } from 'utils/queryHooks/jobs/useJob';
import { useJobs, useJobsQuery } from 'utils/queryHooks/jobs/useJobs';
import useUsersById from 'utils/queryHooks/useUsersById';
import { usePermissions } from 'utils/usePermissions';

import { PREVIOUS_JOBS_PAGE_SIZE } from './constants';
import { usesBatchParentJobs } from './jobQueryOptions';

export interface LoadFromPreviousJobStepProps {
  jobType: JobType;
  selectedJobId: string;
  onSelectedJob: (newValue: Partial<Job>) => void;
  onSelectedJobLoadingChange?: (isLoading: boolean) => void;
  queryScope?: { studyId: string; labId: string };
}

export const LoadFromPreviousJobStep = (props: LoadFromPreviousJobStepProps): ReactElement =>
  props.queryScope ? (
    <ScopedPreviousJobStep key={`${props.queryScope.labId}-${props.queryScope.studyId}`} {...props} />
  ) : (
    <ContextPreviousJobStep {...props} />
  );

const ContextPreviousJobStep = (props: LoadFromPreviousJobStepProps) => {
  const [page, setPage] = useState(1);
  const query = useJobs({
    page,
    pageSize: PREVIOUS_JOBS_PAGE_SIZE,
    withChildren: !usesBatchParentJobs(props.jobType),
    additionalFilters: { types: castArray(props.jobType) },
  });
  return (
    <PreviousJobSelection
      {...props}
      page={page}
      jobs={query.data}
      isJobsLoading={query.isLoading}
      onLoadMore={() => setPage((current) => current + 1)}
    />
  );
};

const ScopedPreviousJobStep = (props: LoadFromPreviousJobStepProps) => {
  const [page, setPage] = useState(1);
  const query = useJobsQuery(
    {
      labId: props.queryScope!.labId,
      filters: { studyId: props.queryScope!.studyId, types: [props.jobType], statuses: [JobStatus.Completed] },
      page,
      pageSize: PREVIOUS_JOBS_PAGE_SIZE,
      withChildren: !usesBatchParentJobs(props.jobType),
    },
    { enabled: Boolean(props.queryScope?.studyId && props.queryScope?.labId), keepPreviousData: true, retry: false }
  );
  if (query.isError)
    return <Alert severity="error">Previous runs could not be loaded. Retry or keep the current configuration.</Alert>;
  return (
    <PreviousJobSelection
      {...props}
      page={page}
      jobs={query.data}
      isJobsLoading={query.isLoading}
      onLoadMore={() => setPage((current) => current + 1)}
    />
  );
};

const PreviousJobSelection = ({
  selectedJobId,
  onSelectedJob,
  onSelectedJobLoadingChange,
  queryScope,
  page,
  jobs,
  isJobsLoading,
  onLoadMore,
}: LoadFromPreviousJobStepProps & {
  page: number;
  jobs?: JobResponse;
  isJobsLoading: boolean;
  onLoadMore: () => void;
}): ReactElement => {
  const [jobOptions, setJobOptions] = useState<Job[]>([]);
  const [selectedJobIdState, setSelectedJobIdState] = useState(selectedJobId || '');
  const [hasMore, setHasMore] = useState(true);
  const [pendingSelectedJobId, setPendingSelectedJobId] = useState<string>(null);
  const [paramsPopoverJobId, setParamsPopoverJobId] = useState<string>(null);
  const [paramsPopoverAnchorEl, setParamsPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const onSelectedJobRef = useRef(onSelectedJob);
  const onSelectedJobLoadingChangeRef = useRef(onSelectedJobLoadingChange);

  useEffect(() => {
    onSelectedJobRef.current = onSelectedJob;
  }, [onSelectedJob]);

  useEffect(() => {
    onSelectedJobLoadingChangeRef.current = onSelectedJobLoadingChange;
  }, [onSelectedJobLoadingChange]);

  const {
    data: selectedJobDetails,
    isLoading: isSelectedJobLoading,
    isError: isSelectedJobError,
  } = useJob(pendingSelectedJobId || selectedJobIdState || '');
  const {
    data: paramsPopoverJobDetails,
    isLoading: isParamsPopoverJobLoading,
    isError: isParamsPopoverJobError,
  } = useJob(paramsPopoverJobId || '');

  const { getUserNameById } = useUsersById({ enabled: !queryScope });
  const { hasPermission } = usePermissions();
  const canViewJsonJobParams = hasPermission(Permission.ViewJsonJobParams);

  const getJobExecutorLabel = useCallback(
    (job: Job) => getUserNameById(job.userId) || job.requestSender || 'Unknown requester',
    [getUserNameById]
  );

  const getJobExecutionTimeLabel = useCallback(
    (job: Job) => formatDateTimeForUser(job.startedAt || job.updatedAt) || 'Unknown execution time',
    []
  );

  useEffect(() => {
    if (jobs) {
      if (jobs.totalJobs > page * PREVIOUS_JOBS_PAGE_SIZE) {
        setHasMore(true);
      } else {
        setHasMore(false);
      }
      if (some(jobs?.jobs, (job) => !includes(map(jobOptions, 'id'), job.id))) {
        setJobOptions((prevOption) => uniqBy(concat(prevOption, jobs?.jobs), 'id'));
      }
    }
  }, [jobOptions, jobs, page]);

  useEffect(() => {
    setSelectedJobIdState(selectedJobId || '');
    if (!selectedJobId) {
      setPendingSelectedJobId(null);
    }
  }, [selectedJobId]);

  useEffect(() => {
    if (!pendingSelectedJobId || isSelectedJobLoading || isEmpty(selectedJobDetails)) {
      return;
    }

    onSelectedJobRef.current(selectedJobDetails);
    setPendingSelectedJobId(null);
  }, [isSelectedJobLoading, pendingSelectedJobId, selectedJobDetails]);

  const handleLoadMore = useCallback(() => {
    onLoadMore();
  }, [onLoadMore]);
  useEffect(() => {
    onSelectedJobLoadingChangeRef.current?.(Boolean(pendingSelectedJobId && isSelectedJobLoading));
  }, [isSelectedJobLoading, pendingSelectedJobId]);

  const handleSelectedJobChange = (newValue: Job) => {
    if (selectedJobIdState === newValue.id) {
      setSelectedJobIdState('');
      setPendingSelectedJobId(null);
      onSelectedJobLoadingChange?.(false);
      onSelectedJob(undefined);
    } else {
      setSelectedJobIdState(newValue.id || '');
      if (!queryScope && !isEmpty(newValue.params)) {
        onSelectedJob(newValue);
      }
      onSelectedJobLoadingChange?.(true);
      setPendingSelectedJobId(newValue.id || null);
    }
  };

  const closeParamsPopover = () => {
    setParamsPopoverJobId(null);
    setParamsPopoverAnchorEl(null);
  };

  const visibleJobOptions = useMemo(() => {
    if (!selectedJobIdState || isEmpty(selectedJobDetails) || some(jobOptions, { id: selectedJobIdState })) {
      return jobOptions;
    }

    return uniqBy(concat([selectedJobDetails], jobOptions), 'id');
  }, [jobOptions, selectedJobDetails, selectedJobIdState]);

  const paramsPopoverJob = paramsPopoverJobDetails || visibleJobOptions.find((job) => job.id === paramsPopoverJobId);
  const paramsPopoverData = paramsPopoverJob?.params || {};
  const paramsPopoverJson = JSON.stringify(paramsPopoverData, null, 2);

  return (
    <Grid container direction="column" spacing={2}>
      {isSelectedJobError && pendingSelectedJobId ? (
        <Grid item>
          <Alert severity="error">
            Error loading the selected job details. Try another job or clear the selection.
          </Alert>
        </Grid>
      ) : null}

      <Grid item>
        <RadioGroup value={selectedJobIdState}>
          <Grid container direction="column" spacing={1.5}>
            {map(visibleJobOptions, (job) => {
              const isSelected = selectedJobIdState === job.id;
              const isLoadingSelectedJob = pendingSelectedJobId === job.id && isSelectedJobLoading;

              return (
                <Grid item key={job.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                    }}
                  >
                    <FormControlLabel
                      value={job.id || ''}
                      onChange={() => handleSelectedJobChange(job)}
                      sx={{ alignItems: 'flex-start', m: 0, px: 1.5, py: 1, width: '100%' }}
                      control={<Radio />}
                      label={
                        <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
                          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {job.name || 'Untitled job'}
                            </Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {isLoadingSelectedJob ? <CircularProgress size={16} /> : null}
                              {canViewJsonJobParams ? (
                                <Tooltip title="View job params JSON">
                                  <IconButton
                                    aria-label={`View params for ${job.name || job.id || 'job'}`}
                                    size="small"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setParamsPopoverJobId(job.id || null);
                                      setParamsPopoverAnchorEl(event.currentTarget);
                                    }}
                                  >
                                    <InfoOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Stack>
                          </Stack>
                          {job.description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 1,
                              }}
                            >
                              {job.description}
                            </Typography>
                          ) : null}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              overflow: 'hidden',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 1,
                            }}
                          >
                            {[
                              job.id,
                              `Executed ${getJobExecutionTimeLabel(job)}`,
                              `Executed by ${getJobExecutorLabel(job)}`,
                              job.status ? humanize(job.status) : null,
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </Typography>
                        </Stack>
                      }
                    />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </RadioGroup>
      </Grid>

      {isJobsLoading && (
        <Grid item xs={12}>
          <CircularProgress />
        </Grid>
      )}
      <Grid item container spacing={1}>
        <Grid item>
          <Button onClick={handleLoadMore} disabled={!hasMore}>
            Load More
          </Button>
        </Grid>
        {selectedJobIdState ? (
          <Grid item>
            <Button
              onClick={() => {
                setSelectedJobIdState('');
                setPendingSelectedJobId(null);
                onSelectedJobLoadingChange?.(false);
                onSelectedJob(undefined);
              }}
            >
              Clear Selection
            </Button>
          </Grid>
        ) : null}
      </Grid>

      <Popover
        open={Boolean(paramsPopoverAnchorEl)}
        anchorEl={paramsPopoverAnchorEl}
        onClose={closeParamsPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { maxWidth: 640, width: 'min(640px, calc(100vw - 32px))' } } }}
      >
        <Box data-testid="previous-job-params-popover" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2">
              Previous Job Params{paramsPopoverJob?.name ? ` - ${paramsPopoverJob.name}` : ''}
            </Typography>
            {!isParamsPopoverJobLoading && !isParamsPopoverJobError ? (
              <CopyableText
                textForCopy={paramsPopoverJson}
                tooltipText="Copy job params JSON"
                copyWithButton="right"
                copyButtonProps={{ 'aria-label': 'Copy job params JSON' }}
                copyIconProps={{ fontSize: 'small' }}
              />
            ) : null}
          </Stack>
          {isParamsPopoverJobLoading ? <CircularProgress size={20} /> : null}
          {isParamsPopoverJobError ? <Alert severity="error">Error loading job params.</Alert> : null}
          {!isParamsPopoverJobLoading && !isParamsPopoverJobError ? (
            <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
              <JobParamsJsonTree data={paramsPopoverData} />
            </Box>
          ) : null}
        </Box>
      </Popover>
    </Grid>
  );
};
